import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Row, Col, Form, Button, Spinner, OverlayTrigger, Tooltip, Modal, Nav } from 'react-bootstrap';
import { BsPencil, BsTrash, BsImage, BsThreeDotsVertical, BsPlusCircle, BsEye } from 'react-icons/bs';
import { FaChevronDown, FaChevronUp } from 'react-icons/fa';
import { useSelector } from 'react-redux';
import StarDivider from '../components/uiComponents/StarDivider';
import Toast from '../components/Toast';
import Pagination from '../components/Pagination';
import MetadataSelect from '../components/MetadataSelect';
import RepresentationCountrySelect from '../components/RepresentationCountrySelect';
import posterService from '../api/posterService';
import prayerCenterService from '../api/prayerCenterService';
import { uploadFile } from '../api/uploadService';
import { useNavigate } from 'react-router-dom';

const TYPE_OPTIONS = [
  { value: 'evenements', label: 'Évènements' },
  { value: 'campagnes', label: 'Campagnes' },
  { value: 'annonces', label: 'Annonces' },
];
const STATUT_OPTIONS = [
  { value: 'prevu', label: 'Prévu' },
  { value: 'en_cours', label: 'En cours' },
  { value: 'termine', label: 'Terminé' },
];
const DAY_OPTIONS = [
  { value: 'monday', label: 'Lundi' },
  { value: 'tuesday', label: 'Mardi' },
  { value: 'wednesday', label: 'Mercredi' },
  { value: 'thursday', label: 'Jeudi' },
  { value: 'friday', label: 'Vendredi' },
  { value: 'saturday', label: 'Samedi' },
  { value: 'sunday', label: 'Dimanche' },
];

const DAY_INDEX_TO_VALUE = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];

function dayOfWeekFromDate(dateStr) {
  if (!dateStr) return '';
  // Parse as local date to avoid timezone shift (YYYY-MM-DD)
  const [y, m, d] = dateStr.split('-').map(Number);
  const day = new Date(y, m - 1, d).getDay();
  return DAY_INDEX_TO_VALUE[day] ?? '';
}
const MONTHS = ['Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin', 'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'];
const YEARS = ['2024', '2025', '2026', '2027'];

const EMPTY_FORM = {
  type_affiche: 'evenements',
  pays_concernes: '',
  prayer_center_id: '',
  titre_evenement: '',
  theme: '',
  description: '',
  date_evenement: '',
  date_fin_evenement: '',
  day_of_week: '',
  time_event: '',
  time_event_fin: '',
  place: '',
  statut: 'prevu',
};

function extractError(err) {
  if (err?.response?.data?.errors?.length) {
    return err.response.data.errors.map((e) => e.message).join(', ');
  }
  return err?.response?.data?.message || err?.message || 'Une erreur est survenue';
}

function formatDate(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' });
}

const typeLabel = (v) => TYPE_OPTIONS.find((t) => t.value === v)?.label || v;
const statutLabel = (v) => STATUT_OPTIONS.find((s) => s.value === v)?.label || v;
const statutStyle = (v) => ({
  padding: '2px 8px', borderRadius: 10, fontSize: 11,
  backgroundColor: v === 'prevu' ? '#fff3cd' : v === 'en_cours' ? '#cce5ff' : '#d4edda',
  color: v === 'prevu' ? '#856404' : v === 'en_cours' ? '#004085' : '#155724',
});

export default function AffichesPage() {
  const userRole = useSelector((s) => s.auth.user?.role);
  const isViewer = userRole === 'viewer';
  const [data, setData] = useState([]);
  const [pagination, setPagination] = useState({ total: 0, totalPages: 1, currentPage: 1 });
  const [tabCounts, setTabCounts] = useState({ upcoming: 0, history: 0 });
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [view, setView] = useState('list');
  const [editItem, setEditItem] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [formErrors, setFormErrors] = useState({});
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [previewTarget, setPreviewTarget] = useState(null);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [openMenuId, setOpenMenuId] = useState(null);
  const [toast, setToast] = useState({ message: '', type: 'success' });
  const [activeTab, setActiveTab] = useState('upcoming'); // 'upcoming' | 'history'
  const [prayerCenters, setPrayerCenters] = useState([]); // for the form dropdown
  const [loadingPrayerCenters, setLoadingPrayerCenters] = useState(false);
  const fileRef = useRef();
  const navigate = useNavigate();
  const [filters, setFilters] = useState({
    type_affiche: '', statut: '', month: '', year: '',
    pays_concernes: '', titre_evenement: '', theme: '',
    page: 1, limit: 10,
  });

  const handleClearFilters = () => {
    setFilters({ type_affiche: '', statut: '', month: '', year: '', pays_concernes: '', titre_evenement: '', theme: '', page: 1, limit: 10 });
  };

  const showToast = (message, type = 'success') => setToast({ message, type });
  const hideToast = () => setToast({ message: '', type: 'success' });

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filters.type_affiche) params.type_affiche = filters.type_affiche;
      if (filters.statut) params.statut = filters.statut;
      if (filters.month) params.month = filters.month;
      if (filters.year) params.year = filters.year;
      if (filters.pays_concernes) params.pays_concernes = filters.pays_concernes;
      if (filters.titre_evenement) params.titre_evenement = filters.titre_evenement;
      if (filters.theme) params.theme = filters.theme;
      params.tab = activeTab;
      params.page = filters.page;
      params.limit = filters.limit;
      const { data: res } = await posterService.list(params);
      setData(res.data);
      setPagination(res.pagination || { total: 0, totalPages: 1, currentPage: 1 });
      setTabCounts(res.tabCounts || { upcoming: 0, history: 0 });
    } catch (err) {
      showToast(extractError(err), 'danger');
    } finally {
      setLoading(false);
    }
  }, [filters, activeTab]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleFilterChange = (e) =>
    setFilters((f) => ({ ...f, [e.target.name]: e.target.value, page: 1 }));

  // Load prayer centers when a country (representation) is selected in the form
  const loadPrayerCentersForRepresentation = useCallback(async (representationId) => {
    if (!representationId) { setPrayerCenters([]); return; }
    setLoadingPrayerCenters(true);
    try {
      const { data: res } = await prayerCenterService.listByRepresentation(representationId);
      // listByRepresentation returns { data: [...] } via sendSuccess
      setPrayerCenters(res.data || []);
    } catch (_) {
      setPrayerCenters([]);
    } finally {
      setLoadingPrayerCenters(false);
    }
  }, []);

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setForm((f) => {
      const updated = { ...f, [name]: value };
      if (name === 'date_evenement') {
        updated.day_of_week = dayOfWeekFromDate(value);
      }
      return updated;
    });
    setFormErrors((fe) => ({ ...fe, [name]: '' }));
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setImageFile(file);
    setImagePreview(URL.createObjectURL(file));
  };

  const validateForm = () => {
    const errors = {};
    if (!form.pays_concernes) errors.pays_concernes = 'Pays requis';
    if (!form.titre_evenement) errors.titre_evenement = 'Titre requis';
    if (!form.description) errors.description = 'Description requise';
    if (!form.date_evenement) errors.date_evenement = 'Date requise';
    if (view === 'add' && !imageFile) errors.image = "L'affiche (image) est requise";
    return errors;
  };

  const buildPayload = () => {
    const payload = {};
    const addIfChanged = (key, value) => {
      const prevValue = editItem?.[key];
      const normalizedValue = value === '' ? null : value;

      if (view === 'add') {
        if (normalizedValue !== null && normalizedValue !== undefined) payload[key] = normalizedValue;
        return;
      }

      const prevNormalized = prevValue === '' ? null : prevValue;
      if (prevNormalized === normalizedValue) return;
      payload[key] = normalizedValue;
    };

    addIfChanged('type_affiche', form.type_affiche);
    addIfChanged('pays_concernes', form.pays_concernes);
    addIfChanged('prayer_center_id', form.prayer_center_id);
    addIfChanged('titre_evenement', form.titre_evenement);
    addIfChanged('theme', form.theme);
    addIfChanged('description', form.description);
    addIfChanged('date_evenement', form.date_evenement);
    addIfChanged('date_fin_evenement', form.date_fin_evenement);
    addIfChanged('day_of_week', form.day_of_week);
    addIfChanged('time_event', form.time_event);
    addIfChanged('time_event_fin', form.time_event_fin);
    addIfChanged('place', form.place);
    addIfChanged('statut', form.statut);
    return payload;
  };

  const handleSave = async () => {
    const errors = validateForm();
    if (Object.keys(errors).length) { setFormErrors(errors); return; }

    setSaving(true);
    setUploadProgress(0);

    try {
      const payload = buildPayload();

      if (imageFile) {
        const { publicUrl } = await uploadFile('posters', imageFile, (progress) => {
          setUploadProgress(progress);
        });
        payload.imageUrl = publicUrl;
      }

      if (view === 'add') {
        await posterService.create(payload);
        showToast('Affiche ajoutée avec succès');
      } else {
        await posterService.update(editItem.id, payload);
        showToast('Affiche mise à jour avec succès');
      }

      setView('list');
      resetForm();
      fetchData();
    } catch (err) {
      showToast(extractError(err), 'danger');
    } finally {
      setSaving(false);
      setUploadProgress(0);
    }
  };

  const resetForm = () => {
    setForm(EMPTY_FORM);
    setEditItem(null);
    setImageFile(null);
    setImagePreview(null);
    setFormErrors({});
    setUploadProgress(0);
    setPrayerCenters([]);
  };

  const handleEditOpen = (row) => {
    setForm({
      type_affiche: row.type_affiche,
      pays_concernes: row.pays_concernes,
      prayer_center_id: row.prayer_center_id || '',
      titre_evenement: row.titre_evenement,
      theme: row.theme,
      description: row.description,
      date_evenement: row.date_evenement?.slice(0, 10) || '',
      date_fin_evenement: row.date_fin_evenement?.slice(0, 10) || '',
      day_of_week: row.day_of_week || dayOfWeekFromDate(row.date_evenement?.slice(0, 10)),
      time_event: row.time_event || '',
      time_event_fin: row.time_event_fin || '',
      place: row.place || '',
      statut: row.statut,
    });
    // Use prayer_centers already embedded in representation from list response
    setPrayerCenters(row.representation?.prayer_centers || []);
    setImagePreview(row.image_url || null);
    setImageFile(null);
    setEditItem(row);
    setFormErrors({});
    setView('edit');
  };

  const handleDelete = async () => {
    try {
      await posterService.remove(deleteTarget.id);
      showToast('Affiche supprimée avec succès');
      setDeleteTarget(null);
      fetchData();
    } catch (err) {
      showToast(extractError(err), 'danger');
      setDeleteTarget(null);
    }
  };

  const openImagePreview = (row) => {
    if (!row?.image_url) return;
    setPreviewTarget(row);
    setOpenMenuId(null);
  };

  const tabData = data;

  return (
    <div>
      <Toast message={toast.message} type={toast.type} onClose={hideToast} />

      <div className="text-center mb-3">
        <h5 style={{ fontFamily: 'serif', letterSpacing: 3, fontWeight: 400 }}>ADMINISTRATIONS DES AFFICHES</h5>
        <StarDivider lineWidth="5%" />
      </div>

      {/* Filters */}
      <div className="bg-white p-3 rounded mb-4" style={{ border: '1px solid #eee' }}>
        <div
          className="d-md-none d-flex justify-content-between align-items-center"
          style={{ cursor: 'pointer', marginBottom: filtersOpen ? 12 : 0 }}
          onClick={() => setFiltersOpen((v) => !v)}
        >
          <span className="fw-semibold" style={{ fontSize: 14 }}>Filtres de recherche</span>
          <span>{filtersOpen ? <FaChevronUp size={18} /> : <FaChevronDown size={18} />}</span>
        </div>
        <div className={filtersOpen ? '' : 'd-none d-md-block'}>
          <Row className="gy-1 gx-3">
            <Col md={10} xs={12}>
              <Row>
                <Col md={3} xs={12}>
                  <Form.Label className="small mb-0 fw-semibold">Type de affiche</Form.Label>
                  <Form.Select name="type_affiche" value={filters.type_affiche} onChange={handleFilterChange} size="sm">
                    <option value="">Choisir un type de affiche</option>
                    {TYPE_OPTIONS.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                  </Form.Select>
                </Col>
                <Col md={3} xs={12}>
                  <Form.Label className="small mb-0 fw-semibold">Titre de l'événement</Form.Label>
                  <Form.Control size="sm" name="titre_evenement" value={filters.titre_evenement} onChange={handleFilterChange} placeholder="Rechercher par titre..." />
                </Col>
                <Col md={3} xs={12}>
                  <Form.Label className="small mb-0 fw-semibold">Thème</Form.Label>
                  <MetadataSelect
                    type="theme"
                    value={filters.theme}
                    onChange={val => setFilters(f => ({ ...f, theme: val, page: 1 }))}
                    placeholder="Tous les thèmes"
                    size="sm"
                    canAdd={false}
                  />
                </Col>
                <Col md={3} xs={12}>
                  <Form.Label className="small mb-0 fw-semibold">Pays concernés</Form.Label>
                  <RepresentationCountrySelect
                    value={filters.pays_concernes}
                    onChange={(val) => setFilters((f) => ({ ...f, pays_concernes: val, page: 1 }))}
                    allowAll
                    allLabel="Tous les pays"
                    size="sm"
                  />
                </Col>
                <Col md={3} xs={12}>
                  <Form.Label className="small mb-0 fw-semibold">Statut</Form.Label>
                  <Form.Select name="statut" value={filters.statut} onChange={handleFilterChange} size="sm">
                    <option value="">Tous les statuts</option>
                    {STATUT_OPTIONS.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
                  </Form.Select>
                </Col>
                <Col md={3} xs={12}>
                  <Form.Label className="small mb-0 fw-semibold">Année</Form.Label>
                  <Form.Select name="year" value={filters.year} onChange={handleFilterChange} size="sm">
                    <option value="">Toutes les années</option>
                    {YEARS.map((y) => <option key={y} value={y}>{y}</option>)}
                  </Form.Select>
                </Col>
                <Col md={3} xs={12}>
                  <Form.Label className="small mb-0 fw-semibold">Mois</Form.Label>
                  <Form.Select name="month" value={filters.month} onChange={handleFilterChange} size="sm">
                    <option value="">Tous les mois</option>
                    {MONTHS.map((m, i) => <option key={m} value={i + 1}>{m}</option>)}
                  </Form.Select>
                </Col>
              </Row>
            </Col>
            <Col md={2} xs={12} className="d-flex flex-wrap align-items-center justify-content-md-end justify-content-center gap-2 mt-2 mt-md-0">
              {['type_affiche', 'statut', 'month', 'year', 'pays_concernes', 'titre_evenement', 'theme'].some((k) => filters[k]) && (
                <Button onClick={handleClearFilters} variant="outline-secondary" size="sm" className="text-uppercase px-2">
                  EFFACER
                </Button>
              )}
              {/* {!isViewer && (
                <Button size="sm" style={{ backgroundColor: '#8b1512', border: 'none' }}
                  onClick={() => { resetForm(); setView('add'); }}>
                  + Nouvelle Affiche
                </Button>
              )} */}
            </Col>
          </Row>
        </div>
      </div>

      {/* Add / Edit form */}
      {!isViewer && (view === 'add' || view === 'edit') && (
        <div style={{ maxWidth: 700, margin: '0 auto 24px' }}>
          <h6 className="fw-bold mb-2">{view === 'add' ? 'Ajouter une affiche' : 'Modifier l\'affiche'}</h6>
          <Form className="bg-white rounded p-3" style={{ border: '1px solid #eee' }}>

            {/* Type */}
            <Form.Group as={Row} className="mb-3 align-items-center">
              <Form.Label column sm={4} className="fw-semibold" style={{ fontSize: 14 }}>Type de affiche :</Form.Label>
              <Col sm={8}>
                <Form.Select name="type_affiche" value={form.type_affiche} onChange={handleFormChange} isInvalid={!!formErrors.type_affiche}>
                  {TYPE_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                </Form.Select>
                {formErrors.type_affiche && <div className="invalid-feedback d-block">{formErrors.type_affiche}</div>}
              </Col>
            </Form.Group>

            {/* Pays concernés */}
            <Form.Group as={Row} className="mb-3 align-items-center">
              <Form.Label column sm={4} className="fw-semibold" style={{ fontSize: 14 }}>Pays concernés :</Form.Label>
              <Col sm={8}>
                <div className="d-flex gap-2 align-items-center">
                  <RepresentationCountrySelect
                    value={form.pays_concernes}
                    onChange={(val, rep) => {
                      setForm((f) => ({ ...f, pays_concernes: val, prayer_center_id: '' }));
                      setFormErrors((fe) => ({ ...fe, pays_concernes: '', prayer_center_id: '' }));
                      // If "Worldwide" is selected, don't load prayer centers
                      if (val === 'worldwide') {
                        setPrayerCenters([]);
                      } else if (rep?.id) {
                        loadPrayerCentersForRepresentation(rep.id);
                      } else {
                        setPrayerCenters([]);
                      }
                    }}
                    placeholder="— Sélectionner un pays —"
                    isInvalid={!!formErrors.pays_concernes}
                    width='90%'
                    allowWorldwide={true}
                  />
                  <button
                    type="button"
                    onClick={() => navigate('/representations?add=true')}
                    style={{
                      background: 'none',
                      border: '1px solid #ccc',
                      borderRadius: 4,
                      padding: '4px 8px',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      color: '#8B0000',
                      flexShrink: 0,
                      height: 38,
                    }}
                  >
                    <BsPlusCircle size={16} />
                  </button>
                </div>
                {formErrors.pays_concernes && <div className="text-danger" style={{ fontSize: 12, marginTop: 2 }}>{formErrors.pays_concernes}</div>}
              </Col>
            </Form.Group>

            {/* Prayer Center */}
            <Form.Group as={Row} className="mb-3 align-items-center">
              <Form.Label column sm={4} className="fw-semibold" style={{ fontSize: 14 }}>Centre de prière :</Form.Label>
              <Col sm={8}>
                {loadingPrayerCenters ? (
                  <div style={{ padding: '6px 0' }}><Spinner size="sm" animation="border" style={{ color: '#8b1512' }} /> Chargement...</div>
                ) : (
                  <Form.Select
                    name="prayer_center_id"
                    value={form.prayer_center_id}
                    onChange={handleFormChange}
                    isInvalid={!!formErrors.prayer_center_id}
                    disabled={!form.pays_concernes || form.pays_concernes === 'worldwide'}
                  >
                    <option value="">
                      {!form.pays_concernes ? '— Sélectionnez d\'abord un pays —' : form.pays_concernes === 'worldwide' ? '— Non applicable (Monde entier) —' : prayerCenters.length === 0 ? '— Aucun centre disponible —' : '— Sélectionner un centre —'}
                    </option>
                    {prayerCenters.map((pc) => (
                      <option key={pc.id} value={pc.id}>{pc.nom}</option>
                    ))}
                  </Form.Select>
                )}
                {formErrors.prayer_center_id && <div className="invalid-feedback d-block">{formErrors.prayer_center_id}</div>}
              </Col>
            </Form.Group>

            {/* Titre */}
            <Form.Group as={Row} className="mb-3 align-items-center">
              <Form.Label column sm={4} className="fw-semibold" style={{ fontSize: 14 }}>Titre de l'événement :</Form.Label>
              <Col sm={8}>
                <Form.Control name="titre_evenement" value={form.titre_evenement} onChange={handleFormChange} placeholder="Retraite Spirituelle des Jeunes" isInvalid={!!formErrors.titre_evenement} />
                {formErrors.titre_evenement && <div className="invalid-feedback d-block">{formErrors.titre_evenement}</div>}
              </Col>
            </Form.Group>

            {/* Thème */}
            <Form.Group as={Row} className="mb-3 align-items-center">
              <Form.Label column sm={4} className="fw-semibold" style={{ fontSize: 14 }}>Thème :</Form.Label>
              <Col sm={8}>
                <MetadataSelect
                  type="theme"
                  value={form.theme}
                  onChange={val => { setForm(f => ({ ...f, theme: val })); setFormErrors(fe => ({ ...fe, theme: '' })); }}
                  placeholder="— Sélectionner un thème —"
                  isInvalid={!!formErrors.theme}
                />
                {formErrors.theme && <div className="text-danger" style={{ fontSize: 12, marginTop: 2 }}>{formErrors.theme}</div>}
              </Col>
            </Form.Group>

            {/* Description */}
            <Form.Group as={Row} className="mb-3 align-items-center">
              <Form.Label column sm={4} className="fw-semibold" style={{ fontSize: 14 }}>Description :</Form.Label>
              <Col sm={8}>
                <Form.Control as="textarea" rows={3} name="description" value={form.description} onChange={handleFormChange} placeholder="Une retraite pour approfondir notre foi..." isInvalid={!!formErrors.description} />
                {formErrors.description && <div className="invalid-feedback d-block">{formErrors.description}</div>}
              </Col>
            </Form.Group>

            {/* Date */}
            <Form.Group as={Row} className="mb-3 align-items-center">
              <Form.Label column sm={4} className="fw-semibold" style={{ fontSize: 14 }}>Date de l'événement :</Form.Label>
              <Col sm={8}>
                <Form.Control type="date" name="date_evenement" value={form.date_evenement} onChange={handleFormChange} isInvalid={!!formErrors.date_evenement} min={new Date().toISOString().split("T")[0]} />
                {formErrors.date_evenement && <div className="invalid-feedback d-block">{formErrors.date_evenement}</div>}
              </Col>
            </Form.Group>

            {/* Day of Week */}
            <Form.Group as={Row} className="mb-3 align-items-center">
              <Form.Label column sm={4} className="fw-semibold" style={{ fontSize: 14 }}>Jour de la semaine :</Form.Label>
              <Col sm={8}>
                <Form.Select name="day_of_week" value={form.day_of_week} onChange={handleFormChange} disabled>
                  <option value="">— Sélectionnez une date —</option>
                  {DAY_OPTIONS.map((d) => <option key={d.value} value={d.value}>{d.label}</option>)}
                </Form.Select>
              </Col>
            </Form.Group>

            {/* Time Event */}
            <Form.Group as={Row} className="mb-3 align-items-center">
              <Form.Label column sm={4} className="fw-semibold" style={{ fontSize: 14 }}>Heure de l'événement :</Form.Label>
              <Col sm={8}>
                <Form.Control type="time" name="time_event" value={form.time_event} onChange={handleFormChange} placeholder="HH:MM" />
              </Col>
            </Form.Group>

            {/* End Date */}
            <Form.Group as={Row} className="mb-3 align-items-center">
              <Form.Label column sm={4} className="fw-semibold" style={{ fontSize: 14 }}>Date de fin :</Form.Label>
              <Col sm={8}>
                <Form.Control
                  type="date"
                  name="date_fin_evenement"
                  value={form.date_fin_evenement}
                  onChange={handleFormChange}
                  isInvalid={!!formErrors.date_fin_evenement}
                  min={form.date_evenement || new Date().toISOString().split('T')[0]}
                />
                {formErrors.date_fin_evenement && (
                  <div className="invalid-feedback d-block">{formErrors.date_fin_evenement}</div>
                )}
                <div style={{ fontSize: 11, color: '#999', marginTop: 2 }}>
                  Laissez vide si l'événement se déroule sur une seule journée
                </div>
              </Col>
            </Form.Group>

            {/* End Time */}
            <Form.Group as={Row} className="mb-3 align-items-center">
              <Form.Label column sm={4} className="fw-semibold" style={{ fontSize: 14 }}>Heure de fin :</Form.Label>
              <Col sm={8}>
                <Form.Control type="time" name="time_event_fin" value={form.time_event_fin} onChange={handleFormChange} placeholder="HH:MM" />
                <div style={{ fontSize: 11, color: '#999', marginTop: 2 }}>
                  Utilisé pour déterminer si l'événement est passé ou à venir
                </div>
              </Col>
            </Form.Group>

            {/* Place */}
            <Form.Group as={Row} className="mb-3 align-items-center">
              <Form.Label column sm={4} className="fw-semibold" style={{ fontSize: 14 }}>Lieu de l'événement :</Form.Label>
              <Col sm={8}>
                <Form.Control name="place" value={form.place} onChange={handleFormChange} placeholder="Ex: Centre de Conférence, Salle A" />
              </Col>
            </Form.Group>

            {/* Affiche image */}
            <Form.Group as={Row} className="mb-3 align-items-center">
              <Form.Label
                column
                sm={4}
                className="fw-semibold"
                style={{ fontSize: 14 }}
              >
                Affiche :
              </Form.Label>

              <Col sm={8}>
                <div
                  className="d-flex align-items-center gap-3"
                  style={{
                    border: `1px solid ${formErrors.image ? '#dc3545' : '#ccc'}`,
                    borderRadius: 4,
                    padding: '8px 12px',
                  }}
                >
                  {imagePreview ? (
                    <img
                      src={imagePreview}
                      alt="preview"
                      style={{
                        width: 48,
                        height: 48,
                        objectFit: 'cover',
                        borderRadius: 4,
                        flexShrink: 0,
                      }}
                    />
                  ) : (
                    <div
                      style={{
                        width: 48,
                        height: 48,
                        backgroundColor: '#f0f0f0',
                        borderRadius: 4,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                      }}
                    >
                      <BsImage size={22} color="#aaa" />
                    </div>
                  )}

                  <span
                    className="flex-grow-1 text-truncate"
                    style={{
                      fontSize: 13,
                      color: imageFile ? '#333' : '#aaa',
                    }}
                  >
                    {imageFile
                      ? imageFile.name
                      : view === 'edit'
                        ? 'Image actuelle'
                        : 'Aucun fichier sélectionné'}
                  </span>

                  <input
                    ref={fileRef}
                    type="file"
                    accept="image/*"
                    style={{ display: 'none' }}
                    onChange={handleFileChange}
                  />

                  <Button
                    variant="outline-secondary"
                    size="sm"
                    type="button"
                    onClick={() => fileRef.current.click()}
                  >
                    {view === 'edit' ? 'Remplacer' : 'Importer'} ⇅
                  </Button>
                </div>

                {/* Image dimension note */}
                <div
                  className="text-danger mt-1"
                  style={{
                    fontSize: 11,
                    lineHeight: 1.4,
                  }}
                >
                  <strong>Format recommandé :</strong> 16:9 (paysage), par exemple
                  1920 × 1080 px.
                </div>

                {uploadProgress > 0 && uploadProgress < 100 && (
                  <div className="mt-2">
                    <div
                      style={{
                        fontSize: 12,
                        color: '#666',
                        marginBottom: 4,
                      }}
                    >
                      Téléchargement: {uploadProgress}%
                    </div>

                    <div
                      style={{
                        height: 8,
                        backgroundColor: '#f0f0f0',
                        borderRadius: 4,
                        overflow: 'hidden',
                      }}
                    >
                      <div
                        style={{
                          height: '100%',
                          backgroundColor: '#8b1512',
                          width: `${uploadProgress}%`,
                          transition: 'width 0.3s',
                        }}
                      />
                    </div>
                  </div>
                )}

                {formErrors.image && (
                  <div
                    className="text-danger"
                    style={{
                      fontSize: 12,
                      marginTop: 4,
                    }}
                  >
                    {formErrors.image}
                  </div>
                )}
              </Col>
            </Form.Group>

            {/* Statut */}
            <Form.Group as={Row} className="mb-3 align-items-center">
              <Form.Label column sm={4} className="fw-semibold" style={{ fontSize: 14 }}>Statut de l'événement :</Form.Label>
              <Col sm={8}>
                <Form.Select name="statut" value={form.statut} onChange={handleFormChange}>
                  {STATUT_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                </Form.Select>
              </Col>
            </Form.Group>

            <div className="text-center mt-4 d-flex gap-3 justify-content-center">
              <Button size="sm" variant="outline-secondary" type="button"
                onClick={() => { setView('list'); resetForm(); }}
                style={{ letterSpacing: 1, paddingInline: 24 }}>
                ANNULER
              </Button>
              <Button size="sm" type="button" disabled={saving} onClick={handleSave}
                style={{ backgroundColor: '#8b1512', border: 'none', letterSpacing: 1, paddingInline: 40 }}>
                {saving ? <Spinner size="sm" animation="border" /> : view === 'add' ? 'ENREGISTRER' : 'ENREGISTRER LES MODIFICATIONS'}
              </Button>
            </div>
          </Form>
        </div>
      )
      }

      {/* List */}
      {
        view === 'list' && (
          <>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12, flexWrap: 'wrap', gap: 8 }}>
              <h6 style={{ fontWeight: 700, margin: 0 }}>Résultat de la recherche</h6>
              {!isViewer && (
                <Button size="sm" style={{ backgroundColor: '#8b1512', border: 'none' }}
                  onClick={() => { resetForm(); setView('add'); }}>
                  + Nouvelle Affiche
                </Button>
              )}
            </div>

            {/* Upcoming / History tabs */}
            <Nav variant="tabs" activeKey={activeTab} onSelect={(k) => { setActiveTab(k); setFilters((f) => ({ ...f, page: 1 })); }} className="mb-3" style={{ borderBottom: '2px solid #e0e0e0' }}>
              <Nav.Item>
                <Nav.Link
                  eventKey="upcoming"
                  style={{
                    color: activeTab === 'upcoming' ? '#8b1512' : '#555',
                    borderColor: activeTab === 'upcoming' ? '#8b1512 #8b1512 #fff' : 'transparent',
                    fontWeight: activeTab === 'upcoming' ? 600 : 400,
                    fontSize: 14,
                  }}
                >
                  À venir
                </Nav.Link>
              </Nav.Item>
              <Nav.Item>
                <Nav.Link
                  eventKey="history"
                  style={{
                    color: activeTab === 'history' ? '#8b1512' : '#555',
                    borderColor: activeTab === 'history' ? '#8b1512 #8b1512 #fff' : 'transparent',
                    fontWeight: activeTab === 'history' ? 600 : 400,
                    fontSize: 14,
                  }}
                >
                  Historique
                </Nav.Link>
              </Nav.Item>
            </Nav>

            {loading ? (
              <div className="text-center py-5"><Spinner animation="border" style={{ color: '#8b1512' }} /></div>
            ) : tabData.length === 0 ? (
              <div className="text-center py-5 text-secondary">
                {activeTab === 'upcoming' ? 'Aucune affiche à venir.' : "Aucune affiche dans l'historique."}
              </div>
            ) : (
              <>
                {/* Desktop table */}
                <div style={{ overflowX: 'auto' }} className="d-none d-md-block table-mcm-wrap">
                  <table className="table-mcm">
                    <thead>
                      <tr>
                        {['Type de affiche', 'Date', 'Pays concernés', 'Thème', 'Titre de l\'evenement', 'Statut', 'Affiche', 'Description', 'Actions'].map((h) => (
                          <th key={h} className="th-mcm">{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {tabData.map((row) => (
                        <tr key={row.id}>
                          <td className="td-mcm">{typeLabel(row.type_affiche)}</td>
                          <td className="td-mcm" style={{ whiteSpace: 'nowrap' }}>{formatDate(row.date_evenement)}</td>
                          <td className="td-mcm">{row.pays_concernes || "-"}</td>
                          <td className="td-mcm">{row.theme || '-'}</td>
                          <td className="td-mcm">{row.titre_evenement}</td>
                          <td className="td-mcm"><span style={statutStyle(row.statut)}>{statutLabel(row.statut)}</span></td>
                          <td className="td-mcm">
                            {row.image_url ? (
                              <button
                                type="button"
                                onClick={() => openImagePreview(row)}
                                aria-label={`Voir l'affiche ${row.titre_evenement || ''}`}
                                style={{ padding: 0, border: 'none', background: 'none', cursor: 'pointer' }}
                              >
                                <img src={row.image_url} alt="affiche" style={{ width: 40, height: 40, objectFit: 'cover', borderRadius: 4 }} />
                              </button>
                            ) : (
                              <div style={{ width: 40, height: 40, backgroundColor: '#f0f0f0', borderRadius: 4, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                <BsImage size={16} color="#aaa" />
                              </div>
                            )}
                          </td>
                          <td className="td-mcm">
                            {row.description?.length > 60 ? (
                              <OverlayTrigger
                                placement="top"
                                overlay={<Tooltip>{row.description}</Tooltip>}
                              >
                                <span style={{ cursor: "pointer" }}>
                                  {row.description.slice(0, 60)}...
                                </span>
                              </OverlayTrigger>
                            ) : (
                              row.description
                            )}
                          </td>
                          <td className="td-mcm" style={{ whiteSpace: 'nowrap' }}>
                            <button className="icon-btn-mcm" onClick={() => openImagePreview(row)} disabled={!row.image_url} title="Voir l'affiche">
                              <BsEye size={15} color={row.image_url ? '#555' : '#bbb'} />
                            </button>
                            {!isViewer && (
                              <>
                                <button className="icon-btn-mcm" onClick={() => handleEditOpen(row)}><BsPencil size={14} color="#555" /></button>
                                <button className="icon-btn-mcm" onClick={() => setDeleteTarget(row)}><BsTrash size={14} color="#8b1512" /></button>
                              </>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Mobile cards */}
                <div className="d-md-none">
                  <div className="d-flex flex-column gap-3">
                    {tabData.map((row) => (
                      <div key={row.id} className="bg-white rounded p-3" style={{ border: '1px solid #eee' }}>
                        <div className="d-flex justify-content-between border-bottom border-muted w-100 pb-3 align-items-start mb-3">
                          <div>
                            <div style={{ fontWeight: 600, color: '#8b1512' }}>{typeLabel(row.type_affiche)}</div>
                            <div className='text-muted fw-semibold' style={{ fontSize: 12 }}>
                              {formatDate(row.date_evenement)}
                            </div>
                          </div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <span className='text-capitalize' style={{
                              padding: '2px 8px', borderRadius: 10, fontSize: 11,
                              backgroundColor: row.statut === 'actif' ? '#d4edda' : '#f8d7da',
                              color: row.statut === 'actif' ? '#155724' : '#721c24',
                              whiteSpace: 'nowrap',
                            }}>
                              {statutLabel(row.statut)}
                            </span>
                            {!isViewer && (
                              <div style={{ position: 'relative' }}>
                                <BsThreeDotsVertical onClick={() => setOpenMenuId(openMenuId === row.id ? null : row.id)} size={18} color="#555" style={{ cursor: 'pointer' }} />
                                {openMenuId === row.id && (
                                  <div
                                    style={{
                                      position: 'absolute', right: 0, top: '100%', zIndex: 100,
                                      backgroundColor: '#fff', border: '1px solid #e0e0e0',
                                      borderRadius: 6, boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
                                      minWidth: 140, overflow: 'hidden', marginTop: 4,
                                    }}
                                  >
                                    <button
                                      type="button"
                                      disabled={!row.image_url}
                                      style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%', padding: '8px 14px', background: 'none', border: 'none', fontSize: 13, cursor: row.image_url ? 'pointer' : 'not-allowed', color: row.image_url ? '#333' : '#aaa' }}
                                      onClick={() => openImagePreview(row)}
                                    >
                                      <BsEye size={13} color={row.image_url ? '#555' : '#aaa'} /> Voir
                                    </button>
                                    <button
                                      type="button"
                                      style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%', padding: '8px 14px', background: 'none', border: 'none', fontSize: 13, cursor: 'pointer' }}
                                      onClick={() => { handleEditOpen(row); setOpenMenuId(null); }}
                                    >
                                      <BsPencil size={13} color="#555" /> Modifier
                                    </button>
                                    <button
                                      type="button"
                                      style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%', padding: '8px 14px', background: 'none', border: 'none', fontSize: 13, color: '#8b1512', cursor: 'pointer' }}
                                      onClick={() => { setDeleteTarget(row); setOpenMenuId(null); }}
                                    >
                                      <BsTrash size={13} color="#8b1512" /> Supprimer
                                    </button>
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                        <div style={{ fontSize: 13, marginBottom: 8 }}>
                          <div className="mb-2">
                            <span className="fw-semibold">Affiche: </span>
                            {row.image_url ? (
                              <button
                                type="button"
                                onClick={() => openImagePreview(row)}
                                style={{ padding: 0, border: 'none', background: 'none', cursor: 'pointer', verticalAlign: 'middle' }}
                              >
                                <img src={row.image_url} alt="affiche" style={{ width: 56, height: 56, objectFit: 'cover', borderRadius: 4 }} />
                              </button>
                            ) : (
                              <span className="text-muted">Aucune image</span>
                            )}
                          </div>
                          <div><span className="fw-semibold">Pays concernés: </span>{row.pays_concernes}</div>
                          <div><span className="fw-semibold">Titre de l'événement: </span>{row.titre_evenement}</div>
                          <div><span className="fw-semibold">Thème: </span>{row.theme}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </>
            )}

            <Pagination
              currentPage={filters.page}
              totalPages={pagination.totalPages}
              onPageChange={(page) => setFilters((f) => ({ ...f, page }))}
            />
          </>
        )
      }

      <Modal show={!!deleteTarget} onHide={() => setDeleteTarget(null)} centered>
        <Modal.Body style={{ padding: 28 }}>
          {/* <button onClick={() => setDeleteTarget(null)} className="modal-close-btn">×</button> */}
          <div style={{ textAlign: 'center', marginBottom: 16 }}>
            <div style={{ width: 48, height: 48, borderRadius: '50%', backgroundColor: '#ffe5e5', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 10px' }}>
              <BsTrash size={22} color="#8B0000" />
            </div>
            <h6 style={{ fontWeight: 700, marginBottom: 2 }}>Supprimer cette affiche ?</h6>
            <p style={{ fontSize: 13, color: '#666', margin: 0 }}>Cette action est irréversible.</p>
          </div>
          {deleteTarget && (
            <div style={{ backgroundColor: '#f7f8fa', borderRadius: 8, padding: '14px 16px', marginBottom: 24, fontSize: 13 }}>
              <div className="mb-2"><span className="fw-semibold">Titre : </span>{deleteTarget.titre_evenement || '—'}</div>
              <div className="mb-2"><span className="fw-semibold">Type : </span>{typeLabel(deleteTarget.type_affiche)}</div>
              <div className="mb-2"><span className="fw-semibold">Pays : </span>{deleteTarget.pays_concernes || '—'}</div>
              <div className="mb-2"><span className="fw-semibold">Thème : </span>{deleteTarget.theme || '—'}</div>
              <div className="mb-2"><span className="fw-semibold">Date : </span>{formatDate(deleteTarget.date_evenement)}</div>
              <div><span className="fw-semibold">Statut : </span>
                <span style={statutStyle(deleteTarget.statut)}>{statutLabel(deleteTarget.statut)}</span>
              </div>
            </div>
          )}
          <div style={{ display: 'flex', gap: 12, justifyContent: 'center' }}>
            <button onClick={() => setDeleteTarget(null)} style={{ padding: '8px 24px', borderRadius: 4, border: '1px solid #ccc', background: '#fff', cursor: 'pointer', fontSize: 13, letterSpacing: 0.5 }}>ANNULER</button>
            <button onClick={handleDelete} style={{ padding: '8px 24px', borderRadius: 4, border: 'none', backgroundColor: '#8B0000', color: '#fff', cursor: 'pointer', fontSize: 13, letterSpacing: 0.5 }}>SUPPRIMER</button>
          </div>
        </Modal.Body>
      </Modal>

      <Modal show={!!previewTarget} onHide={() => setPreviewTarget(null)} centered size="lg">
        <Modal.Body style={{ padding: 24 }}>
          <button onClick={() => setPreviewTarget(null)} className="modal-close-btn">×</button>
          <h6 style={{ fontWeight: 700, marginBottom: 16, paddingRight: 28 }}>
            {previewTarget?.titre_evenement || 'Aperçu de l\'affiche'}
          </h6>
          {previewTarget?.image_url && (
            <div style={{ display: 'flex', justifyContent: 'center', backgroundColor: '#f7f8fa', borderRadius: 8, padding: 12 }}>
              <img
                src={previewTarget.image_url}
                alt={previewTarget.titre_evenement || 'Aperçu de l\'affiche'}
                style={{ maxWidth: '100%', maxHeight: '75vh', objectFit: 'contain', borderRadius: 4 }}
              />
            </div>
          )}
        </Modal.Body>
      </Modal>
    </div >
  );
}
